import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Loader2 } from "lucide-react";
import { z } from "zod";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

const submissionSchema = z.object({
  title: z.string().trim().min(1, "Book title is required").max(200),
  author: z.string().trim().min(1, "Author name is required").max(200),
  genre: z.string().trim().min(1, "Genre is required").max(100),
  description: z.string().trim().min(10, "Please provide a longer description").max(2000),
  quotes: z.string().trim().max(2000).optional(),
  coverUrl: z.string().trim().url("Please enter a valid URL").max(500).or(z.literal("")).optional(),
  purchaseUrl: z.string().trim().url("Please enter a valid URL").max(500).or(z.literal("")).optional(),
});

type FormData = z.infer<typeof submissionSchema>;

export default function Submit() {
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hp, setHp] = useState(""); // honeypot field
  const [form, setForm] = useState<FormData>({
    title: "",
    author: "",
    genre: "",
    description: "",
    quotes: "",
    coverUrl: "",
    purchaseUrl: "",
  });

  const update = (field: keyof FormData, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Honeypot check — bots fill hidden fields
    if (hp) { setSubmitted(true); return; }

    const result = submissionSchema.safeParse(form);
    if (!result.success) {
      const firstError = result.error.errors[0]?.message;
      toast.error(firstError || "Please check your input");
      return;
    }

    setSaving(true);
    const { error } = await supabase.from("book_submissions").insert({
      title: result.data.title,
      author_name: result.data.author,
      genre: result.data.genre,
      description: result.data.description,
      quotes: result.data.quotes || null,
      cover_url: result.data.coverUrl || null,
      purchase_url: result.data.purchaseUrl || null,
    });
    setSaving(false);

    if (error) {
      toast.error("Failed to submit. Please try again.");
      return;
    }

    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="flex flex-1 items-center justify-center px-4">
        <div className="max-w-md text-center">
          <CheckCircle2 className="mx-auto mb-4 text-primary" size={48} />
          <h1 className="mb-2 font-display text-3xl font-extrabold text-foreground">
            Thank You!
          </h1>
          <p className="mb-6 text-muted-foreground">
            Your book has been submitted. We're excited to review it and potentially add it to our collection so readers can discover your work.
          </p>
          <Button
            onClick={() => {
              setSubmitted(false);
              setForm({ title: "", author: "", genre: "", description: "", quotes: "", coverUrl: "", purchaseUrl: "" });
            }}
            variant="outline"
            className="rounded-xl"
          >
            Submit Another Book
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="mx-auto max-w-2xl px-4 py-12">
        <div className="mb-8 text-center">
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-foreground md:text-4xl">
            Submit Your Book
          </h1>
          <p className="mt-2 text-muted-foreground">
            Are you an indie author? Tell us about your book and we'll add it to our recommendation engine.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="title">Book Title *</Label>
              <Input
                id="title"
                value={form.title}
                onChange={(e) => update("title", e.target.value)}
                placeholder="The Great Indie Novel"
                className="rounded-xl"
                maxLength={200}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="author">Author Name *</Label>
              <Input
                id="author"
                value={form.author}
                onChange={(e) => update("author", e.target.value)}
                placeholder="Your name"
                className="rounded-xl"
                maxLength={200}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="genre">Genre *</Label>
            <Input
              id="genre"
              value={form.genre}
              onChange={(e) => update("genre", e.target.value)}
              placeholder="e.g. Literary Fiction, Sci-Fi, Romance..."
              className="rounded-xl"
              maxLength={100}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Book Description *</Label>
            <Textarea
              id="description"
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              placeholder="Tell us what your book is about — the more vivid, the better our AI can recommend it to the right readers."
              className="min-h-[120px] rounded-xl"
              maxLength={2000}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="quotes">Notable Quotes</Label>
            <Textarea
              id="quotes"
              value={form.quotes}
              onChange={(e) => update("quotes", e.target.value)}
              placeholder="Share 1-3 memorable quotes from your book, one per line."
              className="min-h-[80px] rounded-xl"
              maxLength={2000}
            />
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="coverUrl">Cover Image URL</Label>
              <Input
                id="coverUrl"
                value={form.coverUrl}
                onChange={(e) => update("coverUrl", e.target.value)}
                placeholder="https://..."
                className="rounded-xl"
                maxLength={500}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="purchaseUrl">Purchase / Read Link</Label>
              <Input
                id="purchaseUrl"
                value={form.purchaseUrl}
                onChange={(e) => update("purchaseUrl", e.target.value)}
                placeholder="https://..."
                className="rounded-xl"
                maxLength={500}
              />
            </div>
          </div>

          {/* Honeypot — hidden from real users */}
          <div aria-hidden="true" className="absolute left-[-9999px]">
            <label htmlFor="website_url_confirm">Leave blank</label>
            <input id="website_url_confirm" name="website_url_confirm" type="text" tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)} />
          </div>

          <Button type="submit" size="lg" className="w-full rounded-xl font-semibold" disabled={saving}>
            {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...</> : "Submit Book"}
          </Button>
        </form>
      </div>
    </div>
  );
}
